import type { MailboxInfo, MailboxType } from 'owa-client-types';
import type UnifiedGroupType from 'owa-service/lib/contract/UnifiedGroup';
import type { ExternalData } from './ExternalData';

export enum LoadState {
    NotStarted,
    Loading,
    Loaded,
    Error,
}

export interface Characteristics {
    supportsCalendar: boolean;
    supportsContacts: boolean;
    supportsMail: boolean;
    supportsSettings: boolean;
    isMicrosoft365Hosted: boolean;
}

export enum M365AuthProvider {
    AAD,
    MSA,
}

export interface M365Mailbox {
    type: MailboxType;
    name: string;
    displayName: string;
    emailAddress: string;
    logonEmailAddress: string;
    userIdentity: string;
    authProviderType: M365AuthProvider;
    routingHint?: string;
    alternateId?: string;
    accountUniqueId?: string;
    imageUrl?: string;
    profileUrl?: string;
    auxiliaryMailboxGuid?: string;
}

// Provides information about the state of the account
export interface AccountState {
    // The numeric value from the OWAConnectedAccountState in owa-account-store package, this
    // value will only be present for client-web applications that have not been migrated
    // to the Monarch multiple account infrastructure
    connectedAccountState: number;
}

// Specifies the source of the account information
export enum AccountSourceType {
    Office365 = 'Office365',
    OutlookDotCom = 'Outlook',
    Google = 'Google',
    ICloud = 'ICloud',
    Yahoo = 'Yahoo',
    Other = 'Other', //Match with MailboxTypeHint
}

export enum AccountDataType {
    M365Mailbox,
}

export enum M365MailboxType {
    Archive,
    User,
    Connected,
    Group,
    Shared,
    PublicFolder,
    Teams,
}

export interface AdditionalAccountInfo {
    hostingSourceId: string;
    loadState: LoadState;
    sources: AccountSource[];
}

export interface AccountSource {
    sourceId: string;
    sourceType: AccountSourceType;
    dataType: AccountDataType;
    displayName: string;
    characteristics: Characteristics;
    externalData: ExternalData;
    mailboxInfo: MailboxInfo;
}

export interface M365AccountSource extends AccountSource {
    m365mailbox: M365Mailbox;
    dataType: AccountDataType.M365Mailbox;
}

export interface M365UserMailboxAccountSource extends M365AccountSource {
    m365mailboxType: M365MailboxType.User;
    sharedSources: AdditionalAccountInfo;
    groupSources: AdditionalAccountInfo;
    archiveSources: AdditionalAccountInfo; //Online Archive Account Source
    publicFolderSources: AdditionalAccountInfo;
    teamsSources: AdditionalAccountInfo;
}

export interface M365ArchiveMailboxAccountSource extends M365AccountSource {
    m365mailboxType: M365MailboxType.Archive;
}

export interface M365SharedMailboxAccountSource extends M365AccountSource {
    m365mailboxType: M365MailboxType.Shared;
}

export interface M365GroupMailboxAccountSource extends M365AccountSource {
    m365mailboxType: M365MailboxType.Group;
    group: UnifiedGroupType;
}

export interface M365PublicFolderMailboxAccountSource extends M365AccountSource {
    m365mailboxType: M365MailboxType.PublicFolder;
}

export interface M365TeamsMailboxAccountSource extends M365AccountSource {
    m365mailboxType: M365MailboxType.Teams;
}

export interface M365ConnectedMailboxAccountSource extends M365AccountSource {
    m365mailboxType: M365MailboxType.Connected;
    accountState: AccountState;
}

export interface AccountSourceList {
    loadState: LoadState;
    globalSettingsSourceId: string;
    selectedSourceId: string;
    sources: AccountSource[];
    identities: string[]; // TODO: Identities need a separate store
}

export const accountSourceDataTypeChecker = {
    isM365Mailbox: (account?: AccountSource): account is M365AccountSource =>
        account?.dataType === AccountDataType.M365Mailbox,
    isM365UserMailbox: (account?: AccountSource): account is M365UserMailboxAccountSource =>
        accountSourceDataTypeChecker.isM365Mailbox(account) &&
        (account as any).m365mailboxType === M365MailboxType.User,
    isM365ArchiveMailbox: (account?: AccountSource): account is M365ArchiveMailboxAccountSource =>
        accountSourceDataTypeChecker.isM365Mailbox(account) &&
        (account as any).m365mailboxType === M365MailboxType.Archive,
    isM365ConnectedMailbox: (
        account?: AccountSource
    ): account is M365ConnectedMailboxAccountSource =>
        accountSourceDataTypeChecker.isM365Mailbox(account) &&
        (account as any).m365mailboxType === M365MailboxType.Connected,
    isM365GroupMailbox: (account?: AccountSource): account is M365GroupMailboxAccountSource =>
        accountSourceDataTypeChecker.isM365Mailbox(account) &&
        (account as any).m365mailboxType === M365MailboxType.Group,
    isM365SharedMailbox: (account?: AccountSource): account is M365SharedMailboxAccountSource =>
        accountSourceDataTypeChecker.isM365Mailbox(account) &&
        (account as any).m365mailboxType === M365MailboxType.Shared,
};

export const m365MailboxTypeToMailboxType = (m365mailboxType: M365MailboxType): MailboxType => {
    switch (m365mailboxType) {
        case M365MailboxType.Archive:
            return 'ArchiveMailbox';
        case M365MailboxType.Connected:
        case M365MailboxType.User:
            return 'UserMailbox';
        case M365MailboxType.Group:
            return 'GroupMailbox';
        case M365MailboxType.PublicFolder:
            return 'PublicMailbox';
        case M365MailboxType.Shared:
            return 'SharedMailbox';
        case M365MailboxType.Teams:
            return 'TeamsMailbox';
        default:
            const _neverValue: never = m365mailboxType;
            return _neverValue;
    }
};
